import React from 'react';
import Icon from '../../../icon';
import useLanguage from '../../../../hooks/useLanguage';
import {formatDateRange, formatMonthYear} from '../../../../utils';
import {MediaList} from './MediaList';
import {SkillPills} from './SkillPills';

interface EducationItemProps {
  degree?: string;
  school?: string;
  startDate?: string;
  endDate?: string;
  description?: string;
  summary?: string[] | null;
  media?: string[];
  completed?: boolean;
  skills?: Array<{
    name: string;
    rating?: number | null;
    count?: number | null;
  }>;
  className?: string;
  onImageLoad?: (src: string) => void;
}

export function EducationItem({
  degree,
  school,
  startDate,
  endDate,
  description,
  summary,
  media,
  completed,
  className = '',
  skills,
  onImageLoad,
}: EducationItemProps) {
  const [lang] = useLanguage();
  const dateRangeLabel = formatDateRange(startDate, endDate, lang);
  const completedLabel = formatMonthYear(endDate, lang);
  return (
    <div className={`pt-3 break-inside-avoid ${className}`}>
      <div className="bg-gray-100 rounded-lg p-4 flex flex-col gap-3 relative">
        <div className="flex justify-between items-start">
          <div className="flex flex-col gap-1">
            <div className="flex flex-col">
              <h3 className="text-md font-bold leading-tight text-gray-800">
                {degree}
              </h3>
              <p className="text-sm font-semibold leading-tight text-primary">
                {school}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <Icon name="calendar-outline" className="w-4 h-4 text-gray-500" />
            <span className="text-sm font-normal leading-tight text-gray-500">
              {dateRangeLabel}
            </span>
          </div>
        </div>
        {description && (
          <div className="text-md font-normal leading-tight text-gray-800">
            {description}
          </div>
        )}

        {/* Summary Section */}
        {summary && summary.length > 0 && (
          <div className="text-md font-normal leading-tight text-gray-800">
            <ul className="list-disc list-inside ml-2">
              {summary.map((item, index) => (
                <li key={index} className="text-sm">
                  {item}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Media List */}
        <MediaList media={media} onImageLoad={onImageLoad} />
        <SkillPills skills={skills} />

        <div className="flex justify-between items-center mt-2">
          <div className="flex items-center gap-1">
            {completed && (
              <>
                <Icon
                  name="trophy-outline"
                  className="text-[#039560] flex justify-center items-center"
                />
                <span className="text-sm font-normal leading-tight text-[#039560]">
                  Behaald {completedLabel}
                </span>
              </>
            )}
          </div>
          <div className="flex items-center gap-1">
            <Icon name="qr-code-outline" className="w-4 h-4 text-primary" />
            <span className="text-sm font-bold text-primary leading-tight">
              Bekijk mijn profiel op Slip
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
